<script setup lang="ts">
import {
    ArrowBack,
    DesktopOutline,
    ListSharp,
    MoonOutline,
    SunnyOutline,
    ChevronDownSharp,
} from "@vicons/ionicons5"
import type { DropdownOption } from "naive-ui"
import { apiThemeList } from "~/api/theme"

const theme = useTheme()
const route = useRoute()
const router = useRouter()

function handleBack() {
    const config = useRuntimeConfig().public
    window.location.href = config.base_url === "/" ? "/diy" : config.base_url + "/diy"
}

const themeData = shallowRef<LyBase.Theme[]>([])

const themeList = computed(() => {
    return themeData.value.map((item) => {
        return {
            label: item.title,
            key: item.id,
        }
    })
})

// 获取列表
function getList() {
    apiThemeList()
        .then(({ response }) => {
            themeData.value = [response[0], ...response[1]]
        })
        .catch(() => {})
        .finally(() => {})
}

function onSelectTheme(key: number, option: DropdownOption) {
    const theme = useTheme()
    if (theme.value?.id === key) {
        return
    }
    theme.value = {
        id: key,
        title: option.label as string,
    }
    router.push({
        path: "/reload",
        query: {
            path: "/diy/page",
        },
    })
}

onMounted(() => {
    getList()
})

const head = useLocaleHead({
    addDirAttribute: true,
    identifierAttribute: "id",
    addSeoAttributes: true,
})

const title = computed(() => {
    return t(route.meta.title ?? "ly-diy-00001")
})

const { t, locale, locales, setLocale, setLocaleCookie } = useI18n()

const langOptions = computed(() => {
    return locales.value.map((v) => {
        if (typeof v === "string") {
            return {
                label: v,
                value: v,
            }
        }
        return {
            label: v.name,
            value: v.code,
        }
    })
})

function onChange(v: string) {
    setLocale(v).then(() => {
        locale.value = v
        setLocaleCookie(v)
    })
}

const color = useColorTheme()
</script>

<template>
    <Html
        :lang="head.htmlAttrs ? head.htmlAttrs.lang : undefined"
        :dir="head.htmlAttrs ? head.htmlAttrs.dir : undefined"
    >
        <Head>
            <Title>{{ title }}</Title>
            <template v-for="link in head.link" :key="link.id">
                <Link :id="link.id" :rel="link.rel" :href="link.href" :hreflang="link.hreflang" />
            </template>
            <template v-for="meta in head.meta" :key="meta.id">
                <Meta :id="meta.id" :property="meta.property" :content="meta.content" />
            </template>
        </Head>
        <Body>
            <NLayout position="absolute">
                <NLayoutHeader style="height: 50px" bordered>
                    <NPageHeader class="header" @back="handleBack">
                        <template #back>
                            <NButton text>
                                <template #icon>
                                    <NIcon>
                                        <ArrowBack />
                                    </NIcon>
                                </template>
                                {{ $t("ly-diy-00002") }}
                            </NButton>
                        </template>
                        <template #title> </template>
                        <template #subtitle>
                            <NBreadcrumb>
                                <NBreadcrumbItem>
                                    <NDropdown
                                        :options="themeList"
                                        show-arrow
                                        @select="onSelectTheme"
                                    >
                                        <div>
                                            {{ theme?.title }}
                                            <NIcon>
                                                <ChevronDownSharp />
                                            </NIcon>
                                        </div>
                                    </NDropdown>
                                </NBreadcrumbItem>
                                <NBreadcrumbItem>
                                    {{ $route.meta.title ? $t($route.meta.title) : "" }}
                                </NBreadcrumbItem>
                            </NBreadcrumb>
                        </template>
                        <template #extra>
                            <NSpace>
                                <!-- <NButton id="theme-btn" @click="color.nextTheme">
                                    <NIcon>
                                        <SunnyOutline v-if="color.state === 'light'" />
                                        <MoonOutline v-if="color.state === 'dark'" />
                                        <DesktopOutline v-if="color.state === 'auto'" />
                                    </NIcon>
                                </NButton> -->
                                <NSelect
                                    size="small"
                                    :value="locale"
                                    :options="langOptions"
                                    @update:value="onChange"
                                >
                                </NSelect>
                            </NSpace>
                        </template>
                    </NPageHeader>
                </NLayoutHeader>
                <NLayout has-sider position="absolute" style="top: 50px">
                    <LayoutSider />
                    <NLayout embedded>
                        <slot />
                    </NLayout>
                </NLayout>
            </NLayout>
        </Body>
    </Html>
</template>

<style lang="scss" scoped>
.header {
    width: 100%;
    display: flex;
    align-items: center;
    height: 50px;
    padding: 8px 16px;
    &::v-deep() {
        .n-page-header {
            width: 100%;
        }
    }
}
</style>
